import { useId } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { webExtractionSchema } from '@repo/shared-types';

import type {
  WebExtractionConfig as ExtractionConfig,
  WebExtractionRule,
} from '@repo/shared-types';

export const defaultWebExtraction = (): ExtractionConfig => ({
  timeoutMs: 15000,
  headers: [],
  rules: [
    {
      field: 'link',
      pattern: '',
      flags: '',
      group: 0,
      mode: 'first',
      required: true,
      resolveUrl: true,
    },
  ],
});

export const WebExtractionConfig = ({
  value,
  onChange,
}: {
  value: ExtractionConfig;
  onChange: (value: ExtractionConfig) => void;
}) => {
  const id = useId();
  const validation = webExtractionSchema.safeParse(value);
  const changeRule = (index: number, patch: Partial<WebExtractionRule>) =>
    onChange({
      ...value,
      rules: value.rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)),
    });
  const selectClass = 'bg-background border-input h-9 w-full rounded-md border px-2 text-sm';

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-xs">
        Fetch public page HTML once and extract named fields. This does not run the website’s
        JavaScript. Use the Input above for a full URL or a URL containing workflow variables.
      </p>
      <div className="space-y-1">
        <label htmlFor={`${id}-base`} className="text-xs font-medium">
          Base URL (optional)
        </label>
        <Input
          id={`${id}-base`}
          value={value.baseUrl ?? ''}
          placeholder="https://example.com"
          onChange={(e) => onChange({ ...value, baseUrl: e.target.value })}
        />
        <p className="text-muted-foreground text-xs">Used only when the input is a relative URL.</p>
      </div>
      <div className="space-y-1">
        <label htmlFor={`${id}-timeout`} className="text-xs font-medium">
          Fetch timeout (milliseconds)
        </label>
        <Input
          id={`${id}-timeout`}
          type="number"
          min={100}
          max={30000}
          value={value.timeoutMs}
          onChange={(e) => onChange({ ...value, timeoutMs: Number(e.target.value) })}
        />
      </div>
      <details className="rounded-md border p-3">
        <summary className="cursor-pointer text-sm font-medium">Request headers (optional)</summary>
        <p className="text-muted-foreground my-2 text-xs">
          Public pages only. Authentication and cookie headers are not supported.
        </p>
        {(['User-Agent', 'Accept', 'Accept-Language'] as const).map((name) => (
          <div key={name} className="mb-2 space-y-1">
            <label htmlFor={`${id}-${name}`} className="text-xs">
              {name}
            </label>
            <Input
              id={`${id}-${name}`}
              value={value.headers.find((h) => h.name === name)?.value ?? ''}
              placeholder="Use default"
              onChange={(e) =>
                onChange({
                  ...value,
                  headers: [
                    ...value.headers.filter((h) => h.name !== name),
                    ...(e.target.value ? [{ name, value: e.target.value }] : []),
                  ],
                })
              }
            />
          </div>
        ))}
      </details>
      {value.rules.map((rule, index) => (
        <fieldset key={index} className="min-w-0 space-y-3 rounded-md border p-3">
          <legend className="px-1 text-sm font-medium">Extraction rule {index + 1}</legend>
          <div className="space-y-1">
            <label htmlFor={`${id}-${index}-field`} className="text-xs">
              Output field
            </label>
            <Input
              id={`${id}-${index}-field`}
              value={rule.field}
              placeholder="finance_url"
              onChange={(e) => changeRule(index, { field: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor={`${id}-${index}-pattern`} className="text-xs">
              Regex (without surrounding slashes)
            </label>
            <Input
              id={`${id}-${index}-pattern`}
              value={rule.pattern}
              className="font-mono"
              placeholder='href="([^"]+)"'
              onChange={(e) => changeRule(index, { pattern: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label htmlFor={`${id}-${index}-flags`} className="text-xs">
                Flags (i, m, s, u)
              </label>
              <Input
                id={`${id}-${index}-flags`}
                value={rule.flags}
                onChange={(e) => changeRule(index, { flags: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor={`${id}-${index}-group`} className="text-xs">
                Capture group
              </label>
              <Input
                id={`${id}-${index}-group`}
                type="number"
                min={0}
                max={99}
                value={rule.group}
                onChange={(e) => changeRule(index, { group: Number(e.target.value) })}
              />
            </div>
          </div>
          <div className="space-y-1">
            <label htmlFor={`${id}-${index}-mode`} className="text-xs">
              Matches
            </label>
            <select
              id={`${id}-${index}-mode`}
              className={selectClass}
              value={rule.mode}
              onChange={(e) =>
                changeRule(index, { mode: e.target.value === 'all' ? 'all' : 'first' })
              }
            >
              <option value="first">First match</option>
              <option value="all">All matches (up to 100)</option>
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor={`${id}-${index}-format`} className="text-xs">
              Output format (optional)
            </label>
            <Input
              id={`${id}-${index}-format`}
              value={rule.outputFormat ?? ''}
              placeholder="https://example.com/$1"
              onChange={(e) => changeRule(index, { outputFormat: e.target.value || undefined })}
            />
            <p className="text-muted-foreground text-xs">
              Use $0 for the whole match or $1–$99 for captures. Otherwise the selected capture
              group is returned.
            </p>
          </div>
          <label className="flex min-h-9 items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={rule.required}
              onChange={(e) => changeRule(index, { required: e.target.checked })}
            />
            Required — fail if no match is found
          </label>
          <label className="flex min-h-9 items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={rule.resolveUrl}
              onChange={(e) => changeRule(index, { resolveUrl: e.target.checked })}
            />
            Resolve result as an HTTP(S) link relative to the page
          </label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={value.rules.length === 1}
            onClick={() => onChange({ ...value, rules: value.rules.filter((_, i) => i !== index) })}
          >
            Remove rule {index + 1}
          </Button>
        </fieldset>
      ))}
      <Button
        type="button"
        variant="outline"
        disabled={value.rules.length >= 10}
        onClick={() =>
          onChange({
            ...value,
            rules: [
              ...value.rules,
              {
                ...defaultWebExtraction().rules[0],
                field: `field_${value.rules.length + 1}`,
              },
            ],
          })
        }
      >
        Add extraction rule
      </Button>
      {!validation.success && (
        <p role="alert" className="text-destructive text-xs">
          {validation.error.issues
            .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
            .join('; ')}
        </p>
      )}
      <p className="text-muted-foreground text-xs">
        Read fields using your output variable, e.g. {'{{extracted_links.finance_url}}'}. Optional
        missing matches return null (first) or [] (all). A failed extraction clears the output.
      </p>
    </div>
  );
};
